/**
 * UI Preferences Hooks
 *
 * React Query hooks for managing UI preferences (theme, compact mode, locale, timezone)
 * Syncs with backend settings API and local storage
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@tankef/api-client';
import type { UIPreferencesResponse, UpdateUIPreferencesRequest } from '@tankef/core';

import { useSettingsStore } from '../../stores';
import {
  getStoredThemePreference,
  resolveTheme,
  storeThemePreference,
} from '../../stores/settings';

// ============================================================================
// Queries (GET)
// ============================================================================

/**
 * Get current UI preferences and design tokens
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useUIPreferences();
 * const theme = data?.data?.preferences.theme;
 * const isDarkModeAvailable = data?.data?.features.dark_mode_available;
 * ```
 */
export function useUIPreferences() {
  return useQuery({
    queryKey: ['settings', 'ui-preferences'],
    queryFn: () => clientApi.getUIPreferences() as Promise<UIPreferencesResponse>,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

// ============================================================================
// Mutations (PATCH)
// ============================================================================

/**
 * Update UI preferences (theme, compact mode, locale, timezone)
 *
 * @example
 * ```tsx
 * const updatePrefs = useUpdateUIPreferences();
 * updatePrefs.mutate(
 *   { theme: 'dark', compact_mode: true },
 *   {
 *     onSuccess: () => {
 *       showToast('Preferences updated');
 *     },
 *   }
 * );
 * ```
 */
export function useUpdateUIPreferences() {
  const queryClient = useQueryClient();
  const { setTheme, setCompactMode } = useSettingsStore();

  return useMutation({
    mutationFn: (body: UpdateUIPreferencesRequest) =>
      clientApi.updateUIPreferences({
        ...body,
        theme: body.theme === 'system' ? resolveTheme(body.theme) : body.theme,
      }) as Promise<UIPreferencesResponse>,
    onSuccess: (data, body) => {
      // Invalidate preferences query to refetch
      queryClient.invalidateQueries({ queryKey: ['settings', 'ui-preferences'] });

      // Update local store with new preferences
      if (body.theme) {
        storeThemePreference(body.theme);
        setTheme(body.theme);
      } else if (data.data?.preferences.theme) {
        setTheme(getStoredThemePreference() ?? data.data.preferences.theme);
      }
      if (typeof data.data?.preferences.compact_mode === 'boolean') {
        setCompactMode(data.data.preferences.compact_mode);
      }
    },
  });
}
